import * as React from "react";
import { useState } from "react";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { Form, DatePicker, Select, Button, Tooltip } from "antd";
import { getRangePickerValue } from "../Input/DatePickerExtensions";
import { StatementPeriod } from "swan-public-api-client";
import { SelectValue } from "antd/lib/select";
import { RangePickerValue } from "antd/lib/date-picker/interface";

export interface SearchParameters {
  from: Date;
  to: Date;
  period: StatementPeriod;
}

interface Props extends WrappedComponentProps {
  onSearch: (searchParameters: SearchParameters) => void;

  from?: Date;
  onFromChange?: (from: Date | undefined) => void;

  to?: Date;
  onToChange?: (to: Date | undefined) => void;

  period?: StatementPeriod;
  onPeriodChange?: (period: StatementPeriod | undefined) => void;
}

function StatementsSearchFormCmp(props: Props) {
  const [from, setFrom] = useState(props.from);
  const [to, setTo] = useState(props.to);
  const [period, setPeriod] = useState(props.period);

  const onDateChange = (dates: RangePickerValue) => {
    const pickerFrom = dates[0];
    const selectedFrom = pickerFrom?.startOf("date").toDate();
    setFrom(selectedFrom);
    if (props.onFromChange !== undefined) {
      props.onFromChange(selectedFrom);
    }

    const pickerTo = dates[1];
    const selectedTo = pickerTo?.endOf("date").toDate();
    setTo(selectedTo);
    if (props.onToChange !== undefined) {
      props.onToChange(selectedTo);
    }
  };

  const onPeriodChange = (selectedPeriod: StatementPeriod | undefined) => {
    setPeriod(selectedPeriod);
    if (props.onPeriodChange !== undefined) {
      props.onPeriodChange(selectedPeriod);
    }
  }

  const onSearchClick = () => {
    props.onSearch({
      from: from!,
      to: to!,
      period: period!
    });
  }

  const canSearch = from !== undefined && to !== undefined && period !== undefined;
  const searchButton = (
    <Button
      type="primary"
      icon="search"
      disabled={!canSearch}
      onClick={() => onSearchClick()}
    >
      {props.intl.formatMessage({ id: "statementsSearchFrom.search" })}
    </Button>
  );

  return (
    <Form layout="inline">
      <Form.Item label={props.intl.formatMessage({ id: "statementsSearchFrom.date" })}>
        <DatePicker.RangePicker
          showTime={false}
          allowClear
          placeholder={[props.intl.formatMessage({ id: "statementsSearchFrom.from" }), props.intl.formatMessage({ id: "statementsSearchFrom.to" })]}
          disabledDate={x => (x === null || x === undefined) || x.toDate() > new Date()}
          onChange={dates => onDateChange(dates)}
          value={getRangePickerValue(from, to)}
        />
      </Form.Item>
      <Form.Item label={props.intl.formatMessage({ id: "statementsSearchFrom.period" })}>
        <Select
          allowClear
          style={{ width: 100 }}
          onChange={(selectedValue: SelectValue) => onPeriodChange(selectedValue as StatementPeriod | undefined)}
          value={period}
        >
          <Select.Option value={StatementPeriod.Daily}>{props.intl.formatMessage({ id: "statementPeriod.daily" })}</Select.Option>
          <Select.Option value={StatementPeriod.Monthly}>{props.intl.formatMessage({ id: "statementPeriod.monthly" })}</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item>
        {canSearch
          ? searchButton
          : <Tooltip title={props.intl.formatMessage({ id: "statementsSearchFrom.fromDateToDateAndPeriodRequiredValidationMessage" })}>{searchButton}</Tooltip>
        }
      </Form.Item>
    </Form>
  );
}

export const StatementsSearchForm = injectIntl(StatementsSearchFormCmp);
